import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useEffect, useState, useRef, Dispatch, SetStateAction } from "react";
import { useFormStatus } from "react-dom";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import {
  isTvEnv,
  shoudlPreventResponsivenessOnLowerLg,
} from "@/utils/common.helpers";
import { setFocusOnTv } from "@/utils/client.helpers";
import { FocusKeyEnum } from "@/utils/focusKeys.helpers";

export const ResendOTPButton = ({
  setHasOTPBeenSent,
  setResendOTPCounter,
}: {
  setHasOTPBeenSent: Dispatch<SetStateAction<boolean>>;
  setResendOTPCounter: Dispatch<SetStateAction<number>>;
}) => {
  const { t } = useClientTranslations();
  const { pending, data } = useFormStatus();
  const [otpTriggered, setOtpTriggered] = useState(false);
  const _shoudlPreventResponsivenessOnLowerLg =
    shoudlPreventResponsivenessOnLowerLg();
  const { ref, focused } = useFocusable({
    focusable: isTvEnv(),
    focusKey: "resend-otp-button",
    isFocusBoundary: true,
    focusBoundaryDirections: ["down", "right"],
    onEnterPress: () => {
      setOtpTriggered(true);
      setHasOTPBeenSent(false);
      // Submit the form
      const form = ref.current?.closest("form");
      if (form) {
        form.requestSubmit();
      }
    },
    onArrowPress: (direction) => {
      // Handle up arrow to go back to keyboard - focus on last row first key
      if (direction === "up") {
        // Focus on the first key of the last row of the numeric keyboard
        setFocusOnTv(FocusKeyEnum.TV_KEYBOARD_KEY_CLEAR);
        return false;
      }
      return true;
    },
  });

  const handleResend = () => {
    setOtpTriggered(true);
    setHasOTPBeenSent(false);

    // Move focus to TV keyboard immediately to prevent native keyboard from opening
    if (isTvEnv()) {
      setFocusOnTv(FocusKeyEnum.LOGIN_KEYBOARD_KEY_1);
    }

    // Submit the form
    const form = ref.current?.closest("form");
    if (form) {
      form.requestSubmit();
    }
  };

  useEffect(() => {
    if (pending === false && otpTriggered === true) {
      setHasOTPBeenSent(true);
      setResendOTPCounter((prevCounter) => prevCounter + 1);
      setOtpTriggered(false); // Reset the trigger state

      // Move focus to TV keyboard after resend OTP to prevent native keyboard from opening
      if (isTvEnv()) {
        setFocusOnTv(FocusKeyEnum.LOGIN_KEYBOARD_KEY_1);
      }
    }
  }, [pending, data, otpTriggered]);

  return (
    <div className="hide-focus-outline">
      <button
        className="mx-auto mb-6 mt-2 block flex rounded px-8 text-center align-middle text-sm text-stg-highlight underline md:hidden"
        aria-disabled={pending}
        disabled={pending}
        onClick={handleResend}
      >
        {pending === false
          ? t("auth_screen.resend_otp")
          : t("auth_screen.please_wait")}
      </button>
      <button
        ref={ref}
        className={`block flex hidden h-[2.5rem] items-center justify-center rounded px-4 text-center align-middle text-sm underline ${!_shoudlPreventResponsivenessOnLowerLg && isTvEnv() ? "!text-[1.125rem]" : "text-sm"} md:block ${
          focused ? "bg-white text-black" : ""
        }`}
        aria-disabled={pending}
        disabled={pending}
        onClick={handleResend}
      >
        {pending === false
          ? t("auth_screen.resend_otp_button_text")
          : t("auth_screen.please_wait")}
      </button>
    </div>
  );
};
